
import React from 'react';
import {
  BarChart,
  PieChart,
} from '@mui/x-charts';
import {
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Divider,
  useTheme
} from '@mui/material';
import { useGetTotalDeliveredShipmentForCustomerQuery } from "store/services/dashboardApi";
import { format } from 'date-fns';

const CustomerShipmentPerformanceScreen = () => {
  const theme = useTheme();
  const { data, isLoading, isError } = useGetTotalDeliveredShipmentForCustomerQuery();
  const currentYear = new Date().getFullYear();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box textAlign="center" p={4}>
        <Typography color="error">Failed to load shipment data</Typography>
      </Box>
    );
  }

  // Transform data for charts
  const barData = data?.data?.map(item => ({
    month: format(new Date(currentYear, item.month - 1, 1), 'MMM'),
    shipments: item.total
  })) || [];

  const pieData = data?.data
    ?.filter(item => item.total > 0)
    ?.map(item => ({
      id: item.month,
      value: item.total,
      label: format(new Date(currentYear, item.month - 1, 1), 'MMMM')
    })) || [];

  const totalShipments = data?.data?.reduce((sum, item) => sum + item.total, 0) || 0;

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Shipment Analytics
      </Typography>

      <Box mb={4}>
        <Chip
          label={`Year: ${currentYear}`}
          color="primary"
          sx={{ fontSize: '1rem', padding: 1 }}
        />
        <Chip
          label={`Total Delivered Shipments: ${totalShipments}`}
          color="secondary"
          sx={{ fontSize: '1rem', padding: 1, ml: 2 }}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <div className="relative pl-12">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 text-sm font-medium text-white">
              Number of Shipments
            </div>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="h6" gutterBottom>
                Monthly Shipment Performance
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box height={400}>
                <BarChart
                  dataset={barData}
                  xAxis={[{
                    scaleType: 'band',
                    dataKey: 'month',
                    label: 'Months'
                  }]}
                  yAxis={[{}]}
                  series={[{
                    dataKey: 'shipments',
                    label: 'Delivered Shipments',
                    color: theme.palette.primary.main
                  }]}
                  margin={{ left: 80, right: 30 }}
                />
              </Box>
            </Paper>
          </div>
        </Grid>

        <Grid item xs={12} md={4} justifyContent="center">
          <Paper elevation={3} sx={{ p: 3, borderRadius: 4, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Distribution by Month
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box height={400}>
              <PieChart
                series={[{
                  data: pieData,
                  innerRadius: 60,
                  outerRadius: 100,
                  paddingAngle: 2,
                  cornerRadius: 5,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 30, additionalRadius: -10 }
                }]}
                slotProps={{
                  legend: {
                    direction: 'column',
                    position: { vertical: 'middle', horizontal: 'right' },
                    padding: 0,
                  },
                }}
                margin={{ right: 150 }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerShipmentPerformanceScreen;